import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminDomainService } from "@/services/admin-domain.service";
import type {
  DomainRegistrationFilters,
  DomainOperationFilters,
  ReconcileDomainRequest,
  RefundDomainRequest,
  CancelDomainRequest,
} from "@/types/domain";

/**
 * Query keys for domain registrations admin queries.
 *
 * - ['admin', 'domains'] — all domain queries
 * - ['admin', 'domains', 'list'] — read-only list queries
 * - ['admin', 'domains', 'list', filters] — filtered list
 * - ['admin', 'domains', 'operations'] — operations dashboard queries
 * - ['admin', 'domains', 'operations', filters] — filtered operations list
 */
export const domainKeys = {
  all: ["admin", "domains"] as const,
  lists: () => [...domainKeys.all, "list"] as const,
  list: (filters: DomainRegistrationFilters) =>
    [...domainKeys.lists(), filters] as const,
  operations: () => [...domainKeys.all, "operations"] as const,
  operationsList: (filters: DomainOperationFilters) =>
    [...domainKeys.operations(), filters] as const,
};

// ============================================================================
// Queries
// ============================================================================

/**
 * Get a paginated list of domain registrations across all tenants.
 *
 * @param filters - Status filter and pagination
 * @returns Paginated list of domain registrations
 */
export function useAdminDomains(filters: DomainRegistrationFilters) {
  return useQuery({
    queryKey: domainKeys.list(filters),
    queryFn: () => adminDomainService.getAll(filters),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * Get a paginated list of domain registrations that need admin
 * attention (RegistrationFailed, stuck Registering, TransferFailed).
 *
 * @param filters - Pagination and optional stuckMinutes threshold
 * @returns Paginated list of operations-dashboard rows
 */
export function useAdminDomainOperations(filters: DomainOperationFilters) {
  return useQuery({
    queryKey: domainKeys.operationsList(filters),
    queryFn: () => adminDomainService.getOperations(filters),
    staleTime: 15_000,
    gcTime: 5 * 60_000,
  });
}

// ============================================================================
// Mutations
// ============================================================================

/**
 * Invalidates every domain query (both the read-only list and the
 * operations dashboard) so all views reflect a registration change.
 */
function useInvalidateDomains() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: domainKeys.all });
}

/**
 * Retry provisioning a failed domain registration.
 *
 * Invalidates all domain queries on success.
 */
export function useRetryDomain() {
  const invalidate = useInvalidateDomains();

  return useMutation({
    mutationFn: (id: string) => adminDomainService.retry(id),
    onSuccess: (result) => {
      invalidate();
      toast.success("Retry queued", {
        description: `Provisioning re-queued for ${result.fqdn}`,
      });
    },
    onError: (error: Error) => {
      toast.error("Failed to retry provisioning", {
        description: error.message,
      });
    },
  });
}

/**
 * Manually reconcile an out-of-band domain registration.
 *
 * Invalidates all domain queries on success.
 */
export function useReconcileDomain() {
  const invalidate = useInvalidateDomains();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReconcileDomainRequest }) =>
      adminDomainService.reconcile(id, data),
    onSuccess: (result) => {
      invalidate();
      toast.success("Domain reconciled", {
        description: `${result.fqdn} is now ${result.status}`,
      });
    },
    onError: (error: Error) => {
      toast.error("Failed to reconcile domain", {
        description: error.message,
      });
    },
  });
}

/**
 * Issue a partial refund for a failed domain order line.
 *
 * Invalidates all domain queries on success.
 */
export function useRefundDomain() {
  const invalidate = useInvalidateDomains();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RefundDomainRequest }) =>
      adminDomainService.refund(id, data),
    onSuccess: (result) => {
      invalidate();
      toast.success("Refund issued", {
        description: `${result.fqdn} was refunded and cancelled`,
      });
    },
    onError: (error: Error) => {
      toast.error("Failed to issue refund", {
        description: error.message,
      });
    },
  });
}

/**
 * Cancel a domain registration without a refund.
 *
 * Invalidates all domain queries on success.
 */
export function useCancelDomain() {
  const invalidate = useInvalidateDomains();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CancelDomainRequest }) =>
      adminDomainService.cancel(id, data),
    onSuccess: (result) => {
      invalidate();
      toast.success("Domain cancelled", {
        description: `${result.fqdn} was cancelled`,
      });
    },
    onError: (error: Error) => {
      toast.error("Failed to cancel domain", {
        description: error.message,
      });
    },
  });
}

/**
 * Admin-initiated transfer status poll for a TransferPending domain (P2).
 *
 * Re-checks the transfer status at the registrar; on success the backend
 * may advance the domain to Active or leave it TransferPending depending
 * on the registry's response. Invalidates all domain queries on success.
 */
export function useRefreshTransfer() {
  const invalidate = useInvalidateDomains();

  return useMutation({
    mutationFn: (id: string) => adminDomainService.refreshTransfer(id),
    onSuccess: (result) => {
      invalidate();
      toast.success("Transfer status refreshed", {
        description: `${result.fqdn} is now ${result.status}`,
      });
    },
    onError: (error: Error) => {
      toast.error("Failed to refresh transfer status", {
        description: error.message,
      });
    },
  });
}
